import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { analytics } from '../services/analytics';
import { useAuth } from '../context/AuthContext';

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: ${props => props.theme.spacing.xlarge};
  text-align: center;
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${props => props.theme.spacing.large};
  color: #28a745;
`;

const SuccessTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: ${props => props.theme.spacing.medium};
  color: ${props => props.theme.colors.text.primary};
  font-weight: 700;
`;

const SuccessMessage = styled.p`
  font-size: 1.2rem;
  margin-bottom: ${props => props.theme.spacing.xlarge};
  color: ${props => props.theme.colors.text.secondary};
  max-width: 600px;
  line-height: 1.6;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.medium};
  flex-wrap: wrap;
  justify-content: center;
`;

const ActionButton = styled.button`
  padding: ${props => props.theme.spacing.medium} ${props => props.theme.spacing.large};
  background-color: ${props => props.theme.colors.button.primary.background};
  color: ${props => props.theme.colors.button.primary.text};
  border: none;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.medium};
  min-width: 150px;
  
  &:hover {
    background-color: ${props => props.theme.colors.button.primary.hover};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
`;

const SecondaryButton = styled(ActionButton)`
  background-color: transparent;
  color: ${props => props.theme.colors.text.accent};
  border: 1px solid ${props => props.theme.colors.text.accent};
  
  &:hover {
    background-color: ${props => props.theme.colors.text.accent};
    color: white;
  }
`;

const InfoBox = styled.div`
  background-color: ${props => props.theme.colors.background.secondary};
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.large};
  margin-top: ${props => props.theme.spacing.xlarge};
  max-width: 500px;
`;

const InfoTitle = styled.h3`
  margin: 0 0 ${props => props.theme.spacing.medium} 0;
  color: ${props => props.theme.colors.text.primary};
  font-size: 1.1rem;
`;

const InfoText = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.text.secondary};
  font-size: 0.9rem;
  line-height: 1.5;
`;

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [compraId, setCompraId] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    // Track page view
    analytics.trackPageView('/payment-success');
    
    // Obtener compraId del URL si existe
    const purchaseId = searchParams.get('compraId');
    if (purchaseId) {
      setCompraId(purchaseId);
      console.log('Compra confirmada con ID:', purchaseId);
    }
  }, [searchParams]);

  const handleContinueShopping = () => {
    navigate('/');
  };

  const handleViewFavorites = () => {
    navigate('/favorites');
  };

  return (
    <SuccessContainer>
      <SuccessIcon>✅</SuccessIcon>
      
      <SuccessTitle>¡Pago Exitoso!</SuccessTitle>
      
      <SuccessMessage>
        Tu compra ha sido procesada correctamente. Recibirás un email en <strong>{user?.email}</strong> con las imágenes que compraste.
        {compraId && (
          <><br/><br/>
          <strong>Número de orden: #{compraId}</strong>
          </>
        )}
      </SuccessMessage>

      <ActionButtons>
        <ActionButton onClick={handleContinueShopping}>
          Seguir Comprando
        </ActionButton>
        <SecondaryButton onClick={handleViewFavorites}>
          Ver Favoritos
        </SecondaryButton>
      </ActionButtons>

    </SuccessContainer>
  );
};

export default PaymentSuccessPage;